import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { useAuth } from '@/auth/useAuth'
import { AdminApiError } from '@/api/errors'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import BrandMark from '@/components/BrandMark'
import { toast } from '@/lib/toast'

interface LocationState {
  from?: string
}

// 已知的 login errorCode → i18n key 映射(其它走 fallback unknown)
const ERROR_KEY_MAP: Record<string, string> = {
  invalid_credentials: 'auth.login.error.invalid_credentials',
  login_rate_limited: 'auth.login.error.rate_limited',
  invalid_input: 'auth.login.error.invalid_input',
  network_error: 'auth.login.error.network'
}

const LoginSchema = z.object({
  username: z.string().trim().min(3).max(32),
  password: z.string().min(1).max(256)
})

type LoginFormValues = z.infer<typeof LoginSchema>

export default function Login() {
  const { t } = useTranslation()
  const { isAuthenticated, login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      username: '',
      password: ''
    }
  })
  const loginMutation = useMutation({
    mutationFn: ({ username, password }: LoginFormValues) => login(username, password),
    onSuccess: () => {
      const next = (location.state as LocationState | null)?.from ?? '/overview'
      void navigate(next, { replace: true })
    },
    onError: (err) => {
      if (err instanceof AdminApiError) {
        const key = ERROR_KEY_MAP[err.errorCode] ?? 'auth.login.error.unknown'
        if (err.errorCode === 'login_rate_limited' && typeof err.retryAfter === 'number') {
          toast.error(t(key, { seconds: err.retryAfter }))
        } else {
          toast.error(t(key))
        }
      } else {
        toast.error(t('auth.login.error.unknown'))
      }
    }
  })
  const submitting = loginMutation.isPending

  // 已登录访问 /login 直接弹回原入口或 overview
  if (isAuthenticated) {
    const fallback = (location.state as LocationState | null)?.from ?? '/overview'
    return <Navigate to={fallback} replace />
  }

  const handleSubmit = form.handleSubmit((values) => {
    if (submitting) return
    loginMutation.mutate(values)
  })
  const usernameField = form.register('username')
  const passwordField = form.register('password')

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm space-y-4 rounded-lg border border-border bg-card p-6 shadow-sm"
      >
        <div className="flex items-start gap-3">
          <BrandMark data-testid="admin-login-brand-mark" className="h-9 w-9" />
          <h1 className="text-lg font-semibold">{t('auth.login.title')}</h1>
        </div>

        <Field label={t('auth.login.username')} labelFor="username">
          <Input
            id="username"
            type="text"
            autoComplete="username"
            autoFocus
            required
            minLength={3}
            maxLength={32}
            {...usernameField}
            className="h-auto w-full px-3 py-2 shadow-none"
          />
        </Field>

        <Field label={t('auth.login.password')} labelFor="password">
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            minLength={1}
            {...passwordField}
            className="h-auto w-full px-3 py-2 shadow-none"
          />
        </Field>

        <Button type="submit" loading={submitting} className="w-full">
          {t('auth.login.submit')}
        </Button>
      </form>
    </div>
  )
}
